"use client";
import { FullPageLoader } from "@cmp-src/loaders/FullPageLoader/FullPageLoader";
import { MapEditSelectedZones } from "@cmp-src/map/InteractiveZones/MapEditSelectedZones";
import { IResource } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { useBooleanState } from "@gettactic/helpers/src";
import { UseSvgMapInteractionsReturn } from "@gettactic/helpers/src/map/svg/useSvgMapInteractions";
import React from "react";
import { MapViewSvgFloorItem } from "@/features/map/components/MapView/FloorItem/MapViewSvgFloorItem";
import { MapInteractiveZones } from "@/features/map/components/MapView/MapInteractiveZones";
import { MapPulseHighlights } from "@/features/map/components/MapView/MapPulseHighlights";
import { MapStackSwitcher } from "@/features/map/components/MapView/MapStackSwitcher";
import { MapViewOverlays } from "@/features/map/components/MapView/MapViewOverlays";
import { UseMapViewSvgEditReturn } from "@/features/map/components/MapView/MapViewSvg/useMapViewSvgEdit";
import { useOnMapLoaded } from "@/features/map/components/MapView/useOnMapLoaded";
import { UploadMapChanges } from "@/features/map/components/UploadMapChanges";
import { useMap } from "@/features/map/MapProvider";
import { useReserve } from "@/features/reserve/providers/context";
import { useLayout } from "@/providers/LayoutProvider/context";

const MAP_CONTAINER_TOP_BOUNDARY = 0;

const classes = {
  FloorsContainer: "absolute top-24 md:top-0 right-0 bottom-0 left-0",
  FloorsContainerEmbedded: "absolute top-0 md:top-0 right-0 bottom-0 left-0",
  FloorsContainerEditing: "absolute top-0 right-0 bottom-0 left-0",
  PerspectiveContainer: "h-full w-full overflow-hidden"
};

export interface MapViewProps {
  embedded?: boolean;
  editor?: UseMapViewSvgEditReturn;
  allowFloorSwitcher?: boolean;
  allowOverlays?: boolean;
  initialFocussedResource?: string;
  /** Map coordinates to centre/zoom on once the SVG is ready (e.g. a pin). */
  initialFocussedPoint?: { x: number; y: number };
  showMiniMap?: boolean;
  allowInteractionsOnFocussedResource?: boolean;
  isOnReservationModal?: boolean;
  isReservationCardIsOpen?: boolean;
  controlsPortalTarget?: HTMLElement | null;
  /** Overrides the target zoom used when focusing (embedded previews). */
  focusZoom?: number;
  /** Pulse-highlight `initialFocussedResource` on load without panning/
   *  zooming the map to it. */
  disableInitialFocusZoom?: boolean;
  /** Forwarded to MapViewOverlays — see its prop doc. */
  onReserveResource?: (resource: IResource) => void;
}

export const MapViewSvg: React.FC<MapViewProps> = ({
  embedded,
  editor,
  allowFloorSwitcher = true,
  allowOverlays = true,
  initialFocussedResource,
  initialFocussedPoint,
  showMiniMap = true,
  isOnReservationModal: propsIsOnReservationModal = false,
  isReservationCardIsOpen: propsIsReservationCardIsOpen = false,

  allowInteractionsOnFocussedResource = false,
  controlsPortalTarget,
  focusZoom,
  disableInitialFocusZoom,
  onReserveResource
}) => {
  const {
    floor,
    isMapBuilderMap,
    selectedResource,
    setOpenResourceModal,
    poppers,
    floors,
    zonesInfo,
    mapChangesCount,
    setMapChangesCount,
    setFloor,
    events,
    fullscreenMapDisclosure,
    viewingDivision,
    isPinRequestMode
  } = useMap();

  const { isReserveOpen } = useReserve();

  // Campus (division) map view: rendered through the same floor pipeline as
  // a pseudo-floor. Only the fields the map pipeline reads are populated —
  // divisions aren't resources, so a full IResource doesn't exist for them.
  const divisionFloor = React.useMemo(() => {
    if (!viewingDivision?.map_data) return null;
    return {
      id: viewingDivision.id,
      name: viewingDivision.name,
      map_data: viewingDivision.map_data
    } as IResource;
  }, [viewingDivision]);

  // Builder-map handling applies to division maps too (they're always
  // builder maps — no legacy SVG path for divisions).
  const isBuilderMapView = isMapBuilderMap || !!divisionFloor;

  const isMapEditing =
    editor?.isResourcePanelEditorOpen || editor?.isMapBuilderEditorOpen;

  // Reserve the sidebar gap only while the side panel is visible; when
  // collapsed (fullscreen) or in campus mode (floating card) the map must
  // expand to the left edge instead of leaving a dead strip.
  const floorsContainerClassName = cn(
    classes.FloorsContainer,
    fullscreenMapDisclosure.isOpen || viewingDivision
      ? "md:left-0"
      : "md:left-[425px]"
  );

  const [mapContainerRef, setMapContainerRef] =
    React.useState<HTMLDivElement | null>(null);

  const { setSelectedFloor: setLayoutFloor } = useLayout();

  const floorContainer = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<HTMLDivElement | null>(null);
  const svgRef = React.useRef<SVGSVGElement | null | undefined>(null);
  const isMapLoaded = useBooleanState();
  const isMaLoadedRef = React.useRef(false);
  const isMapSwitcherReady = useBooleanState();
  const isSwitchingWithFloorSwitcher = useBooleanState();

  const svgMapInteractions = React.useRef<UseSvgMapInteractionsReturn | null>(
    null
  );
  const [initFocusResource, setInitFocusResource] = React.useState<
    string | undefined
  >(initialFocussedResource);
  const [currentFloor, setCurrentFloor] = React.useState<IResource | null>(
    floor
  );
  // Delayed like currentFloor so the floor item only remounts after the
  // readiness flags reset — mounting earlier would report ready and then
  // get blanked by the reset (the same race floor switches avoid).
  const [currentDivisionFloor, setCurrentDivisionFloor] =
    React.useState<IResource | null>(divisionFloor);

  const displayedMapId = divisionFloor?.id ?? floor?.id ?? null;
  const isChangingFloor =
    floor?.id !== currentFloor?.id ||
    (divisionFloor?.id ?? null) !== (currentDivisionFloor?.id ?? null);

  const {
    onMapLoaded,
    onMapUnloaded,
    isLoading: isMapLoaderRunning
  } = useOnMapLoaded(divisionFloor ?? floor, (svg) => {
    svgRef.current = svg;
  });

  // Which floor's map has already reported ready. Map-builder maps mount
  // synchronously (no SVG fetch) and fire onMapReady before the 500ms
  // floor-switch timer below — resetting readiness for a floor that is
  // already loaded would blank the map forever, since the svg ref only
  // attaches once and nothing re-reports ready.
  const loadedFloorIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    onMapUnloaded();
    const timeout = setTimeout(() => {
      setCurrentFloor(floor);
      setCurrentDivisionFloor(divisionFloor);
      isMapSwitcherReady.false();
      if (loadedFloorIdRef.current !== displayedMapId) {
        isMapLoaded.false();
        isMaLoadedRef.current = false;
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [displayedMapId]);

  const availableFloors =
    floors?.filter((x) => !!x.map_url || !!x.map_data) || [];

  const updateModalPosition = () => {
    if (!poppers.updateResDetails || !poppers.referenceResDetailsElement) {
      return;
    }
    if (
      poppers.referenceResDetailsElement.getBoundingClientRect().top <
      MAP_CONTAINER_TOP_BOUNDARY
    ) {
      setOpenResourceModal(false);
    }
    if (selectedResource) {
      poppers.updateResDetails();
    }
  };

  const containerBounds = React.useMemo(() => {
    return mapContainerRef
      ? mapContainerRef.getBoundingClientRect()
      : { top: 0, left: 0, width: 0, height: 0 };
  }, [mapContainerRef]);

  const itemContainerRef = React.useRef<HTMLDivElement | null>(null);
  const isFloorLoading = isChangingFloor || !isMapLoaded.state;

  React.useEffect(() => {
    if (!isFloorLoading) isSwitchingWithFloorSwitcher.false();
  }, [isFloorLoading]);

  // Recenter map when reservation modal state changes (drawer hide/show)
  // But don't blur the focused resource if the reserve modal is open
  React.useEffect(() => {
    if (!isMaLoadedRef.current) return;

    // Don't trigger blur if reserve modal is open - this prevents
    // the modal from closing when exiting fullscreen to make a reservation
    if (isReserveOpen) return;

    const timeout = setTimeout(() => {
      events.trigger("onFocusResource", null);
    }, 500);

    return () => clearTimeout(timeout);
  }, [fullscreenMapDisclosure.isOpen, isReserveOpen]);

  const handleMapReady = () => {
    if (isMaLoadedRef.current) return;

    isMaLoadedRef.current = true;
    isMapLoaded.true();

    if (initFocusResource) {
      setTimeout(() => {
        events.trigger(
          "onFocusResource",
          { id: initFocusResource } as IResource,
          disableInitialFocusZoom ? { noZoom: true } : undefined
        );
        setInitFocusResource(undefined);
      }, 500);
    }
  };

  if (!availableFloors) return <div />;

  const isAnyFloorSelected = !!currentFloor;
  const showFloorSwitcher = !currentFloor && !floor;

  return (
    <>
      <div className={"fixed top-0 left-1/2 z-modal p-2"}>
        {!!mapChangesCount && !!svgRef.current && !!currentFloor && (
          <UploadMapChanges
            floor={currentFloor}
            svg={svgRef.current}
            mapChanges={mapChangesCount}
            setMapChanges={setMapChangesCount}
          />
        )}
      </div>
      <div className={"fixed top-0 left-1/2 z-modal p-2"}>
        {!!mapChangesCount && !!svgRef.current && !!currentFloor && (
          <UploadMapChanges
            floor={currentFloor}
            svg={svgRef.current}
            mapChanges={mapChangesCount}
            setMapChanges={setMapChangesCount}
          />
        )}
      </div>
      <div
        onClick={updateModalPosition}
        className={"floor-container"}
        ref={floorContainer}
      >
        <div className={"v2-map"}>
          {allowFloorSwitcher &&
          (showFloorSwitcher || isSwitchingWithFloorSwitcher.state) ? (
            <MapStackSwitcher
              floorContainerRef={floorContainer}
              onReady={() => isMapSwitcherReady.true()}
              onFloorSelected={(floor) => {
                isSwitchingWithFloorSwitcher.true();
                setFloor(floor);
              }}
            />
          ) : undefined}
          {currentDivisionFloor || currentFloor ? (
            <div
              className={cn(
                "map-container",
                // `left` animates alongside opacity so entering/leaving
                // campus mode expands/contracts the map smoothly.
                "transition-[opacity,left] duration-300",
                // Pin cursor while dropping a workplace-request pin (see globals.css).
                isPinRequestMode && !embedded && "pin-drop-cursor",
                "transition-opacity",
                isFloorLoading ? "opacity-0" : "opacity-100",
                isMapEditing
                  ? classes.FloorsContainerEditing
                  : embedded
                    ? classes.FloorsContainerEmbedded
                    : floorsContainerClassName
              )}
              data-testid="map-canvas"
              data-allow-zoom
              ref={setMapContainerRef}
            >
              {!!mapContainerRef && !!floorContainer.current && (
                <div
                  className={classes.PerspectiveContainer}
                  style={{
                    perspective: "1200px"
                  }}
                >
                  <div
                    className={"item-container"}
                    style={{
                      width: containerBounds.width,
                      height: containerBounds.height,
                      transformStyle: "preserve-3d",
                      transition: !isAnyFloorSelected
                        ? "transform 0.8s ease"
                        : undefined,

                      pointerEvents: "none"
                    }}
                    ref={itemContainerRef}
                  >
                    <MapViewSvgFloorItem
                      allowInteractionsOnFocussedResource={
                        allowInteractionsOnFocussedResource
                      }
                      showMiniMap={showMiniMap && !embedded}
                      embedded={embedded}
                      floor={(currentDivisionFloor || currentFloor) as IResource}
                      focusZoom={focusZoom}
                      focusPoint={initialFocussedPoint}
                      mapRef={mapRef}
                      key={(currentDivisionFloor || currentFloor)?.id}
                      index={
                        currentFloor
                          ? (floors?.findIndex(
                              (f) => f.id === currentFloor.id
                            ) ?? -1)
                          : -1
                      }
                      isVisible={true}
                      isMapEditing={
                        editor?.isResourcePanelEditorOpen ||
                        editor?.isMapBuilderEditorOpen
                      }
                      floorContainerRef={floorContainer}
                      mapContainerRef={{ current: mapContainerRef }}
                      onMapReady={(floor, svg, mapInteractions) => {
                        //if (floor?.id === currentFloor?.id) return;
                        if (isMapLoaded.state) return;
                        svgMapInteractions.current = mapInteractions;
                        onMapLoaded(null, svg);
                        setLayoutFloor(floor);
                        loadedFloorIdRef.current = floor?.id ?? null;
                        // Map-builder maps have no clone overlay (whose
                        // onReady used to drive this) — the base SVG being
                        // ready is the ready signal.
                        if (isBuilderMapView) handleMapReady();
                      }}
                      onMapReadyEditing={(_, svg) => {
                        editor?.startEdit({ current: svg });
                      }}
                    >
                      {!!(currentDivisionFloor || currentFloor) &&
                        (editor?.isResourcePanelEditorOpen ? (
                          <MapEditSelectedZones
                            selectedZones={editor.selectedNodes}
                            zonesInfo={zonesInfo}
                            className={"absolute top-0 left-0"}
                          />
                        ) : isBuilderMapView ? (
                          // Map-builder maps: spot states and clicks render
                          // natively inside the base SVG (via the
                          // interactive-zones record) — only the hover/focus
                          // pulse visuals need a layer on top.
                          <MapPulseHighlights svgRef={svgRef} />
                        ) : (
                          <MapInteractiveZones
                            svgRef={svgRef}
                            isMapBuilder={false}
                            onReady={handleMapReady}
                          />
                        ))}
                    </MapViewSvgFloorItem>
                  </div>
                </div>
              )}
              {/* MiniMap and zoom controls hidden for embedded version
              {embedded &&
                showMiniMap &&
                !isMapEditing &&
                svgMapInteractions.current && (
                  <MapViewSvgMiniMap
                    mapInteractor={svgMapInteractions.current}
                    portalTarget={controlsPortalTarget || null}
                    embedded={true}
                  />
                )} */}
            </div>
          ) : undefined}
        </div>
      </div>
      {allowOverlays && (
        <MapViewOverlays
          zonesInfo={zonesInfo}
          svgMapRef={svgRef}
          mapContainerRef={{ current: mapContainerRef }}
          onReserveResource={onReserveResource}
        />
      )}
      <FullPageLoader
        className={
          embedded ? classes.FloorsContainerEmbedded : floorsContainerClassName
        }
        isLoading={
          showFloorSwitcher ? !isMapSwitcherReady.state : isFloorLoading
        }
      />
    </>
  );
};
